import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Save, Send, FileText, User, Calendar, Package, DollarSign, AlertCircle, Search, RefreshCw, Loader2 } from 'lucide-react';
import NewClientModal from './NewClientModal';
import { VirtualizedProductDropdown } from './VirtualizedDropdown';
import { useApp } from '../App';

const Facturacion = () => {
  const { siigoProducts, loadSiigoProducts, preloadedCustomers, preloadCustomers } = useApp();
  
  // Estado de clientes - cargados directamente aquí
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  
  const createNewItem = () => ({
    id: Date.now().toString() + Math.random(),
    product_id: '',
    product_code: '',
    description: '',
    warehouse: '',
    quantity: '',
    unitPrice: '',
    discount: 0,
    tax: 19,
    retention: 0,
    notes: ''
  });

  // Formato pesos colombianos
  const formatCOP = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const [items, setItems] = useState([createNewItem()]);
  const [payments, setPayments] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [observations, setObservations] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [toast, setToast] = useState(null);
  
  // Datos de Siigo (excepto clientes que se manejan en OptimizedCustomerSelector)
  const [products, setProducts] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modal de Nuevo Cliente
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientType, setClientType] = useState('person'); // 'person' o 'company'
  
  // Búsqueda de clientes (mantenemos clientSearch para compatibilidad)
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Cargar clientes al iniciar
  useEffect(() => {
    const loadCustomers = async () => {
      setCustomersLoading(true);
      // Usar inmediatamente clientes precargados del contexto si estan disponibles
      if (preloadedCustomers && preloadedCustomers.length > 100) {
        const consumidorFinalEnContexto = preloadedCustomers.find(c => c.identification === '222222222222');
        if (consumidorFinalEnContexto) {
          setCustomers(preloadedCustomers);
        } else {
          const consumidorFinal = {
            id: 'consumidor-final',
            identification: '222222222222',
            name: ['CONSUMIDOR', 'FINAL']
          };
          setCustomers([consumidorFinal, ...preloadedCustomers]);
        }
        setCustomersLoading(false);
        return;
      }
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/customers`);
        const data = await res.json();
        if (data.success && data.customers && data.customers.length > 0) {
          const consumidorFinal = {
            id: 'consumidor-final',
            identification: '222222222222',
            name: ['CONSUMIDOR', 'FINAL']
          };
          setCustomers([consumidorFinal, ...data.customers]);
          console.log('✅ Clientes cargados:', data.customers.length);
        } else if (preloadCustomers) {
          // API no devolvio clientes (loading o error): pedir al contexto que los precargue
          preloadCustomers();
        }
      } catch (e) {
        console.error('Error cargando clientes:', e);
        if (preloadCustomers) preloadCustomers();
      } finally {
        setCustomersLoading(false);
      }
    };
    loadCustomers();
  }, []);

  // Si los clientes precargados llegan despues, usarlos automaticamente
  useEffect(() => {
    if (customers.length <= 1 && preloadedCustomers && preloadedCustomers.length > 100) {
      const consumidorFinalEnContexto = preloadedCustomers.find(c => c.identification === '222222222222');
      if (consumidorFinalEnContexto) {
        setCustomers(preloadedCustomers);
      } else {
        const consumidorFinal = {
          id: 'consumidor-final',
          identification: '222222222222',
          name: ['CONSUMIDOR', 'FINAL']
        };
        setCustomers([consumidorFinal, ...preloadedCustomers]);
      }
    }
  }, [preloadedCustomers, customers.length]);

  // Filtrar clientes al buscar
  useEffect(() => {
    if (clientSearch && clientSearch.length >= 2) {
      const search = clientSearch.toLowerCase();
      const filtered = customers.filter(c => {
        const name = Array.isArray(c.name) ? c.name.join(' ') : (c.name || '');
        return name.toLowerCase().includes(search) || (c.identification || '').includes(search);
      }).slice(0, 30);
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers([]);
    }
  }, [clientSearch, customers]);

  const [newClientData, setNewClientData] = useState({
    type: '',
    identification: '',
    check_digit: '',
    name: '',
    commercial_name: '',
    branch_office: 0,
    active: true,
    vat_responsible: false,
    fiscal_responsibilities: [],
    address: {
      address: '',
      city: {
        country_code: 'Co',
        state_code: '',
        city_code: ''
      }
    },
    phones: [{
      indicative: '57',
      number: '',
      extension: ''
    }],
    contacts: [{
      first_name: '',
      last_name: '',
      email: '',
      phone: {
        indicative: '57',
        number: ''
      }
    }]
  });

  // Cargar vendedor guardado al montar
  useEffect(() => {
    const savedSeller = localStorage.getItem('facturacion_vendedor');
    if (savedSeller) {
      const seller = JSON.parse(savedSeller);
      setSelectedSeller(seller);
      setSellerName(seller.id);
    }
  }, []);

  // El filtrado de clientes ahora se maneja en el componente OptimizedCustomerSelector

  // Constantes para caché de datos de facturación (no clientes)
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos para datos livianos
  const CACHE_KEY_FACTURACION = 'facturacion_data_cache';

  // Función para verificar si el caché es válido y tiene datos
  const isCacheValid = (cacheKey) => {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return false;
    try {
      const { timestamp, data } = JSON.parse(cached);
      const isTimeValid = (Date.now() - timestamp) < CACHE_DURATION;
      return isTimeValid && data && data.document_types?.length > 0 && data.payment_types?.length > 0;
    } catch {
      return false;
    }
  };

  // Función para limpiar caché y forzar recarga
  const forceRefreshData = () => {
    localStorage.removeItem(CACHE_KEY_FACTURACION);
    loadSiigoData(true);
  };

  // Función para obtener datos del caché
  const getCachedData = (cacheKey) => {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data } = JSON.parse(cached);
        return data;
      }
    } catch (e) {
      console.warn('Error leyendo caché:', e);
    }
    return null;
  };

  // Función para guardar en caché (solo datos de facturación, no clientes)
  const setCachedData = (cacheKey, data) => {
    try {
      const dataStr = JSON.stringify({ data, timestamp: Date.now() });
      // Solo guardar si es menor a 1MB
      if (dataStr.length < 1000000) {
        localStorage.setItem(cacheKey, dataStr);
      }
    } catch (e) {
      console.warn('Error guardando en caché:', e);
    }
  };

  // Cargar datos de Siigo al montar
  useEffect(() => {
    loadSiigoData();
  }, []);

  // Si los productos llegan despues por el contexto compartido y aun no tenemos productos cargados,
  // mapearlos automaticamente (resuelve el caso QuotaExceededError donde localStorage estaba vacio)
  useEffect(() => {
    if (products.length === 0 && siigoProducts && siigoProducts.length > 0) {
      const mapped = siigoProducts.map(p => {
        const priceList = p.prices?.[0]?.price_list || [];
        const efectivo = priceList.find(pl => pl.position === 1)?.value || 0;
        const tarjeta = priceList.find(pl => pl.position === 2)?.value || 0;
        let impuestoNombre = 'No aplica';
        if (p.taxes && p.taxes.length > 0) {
          const taxId = p.taxes[0].id;
          if (taxId === 5848) impuestoNombre = 'IVA 19%';
          else if (taxId === 5849) impuestoNombre = 'IVA 5%';
          else if (taxId === 13939) impuestoNombre = 'IVA 0%';
          else impuestoNombre = p.taxes[0].name || 'No aplica';
        }
        return {
          id: p.id,
          codigo: p.code || '',
          nombre: p.name || '',
          categoria: p.account_group?.name || 'Sin categoría',
          cantidad: p.available_quantity || 0,
          precio_lista1: efectivo,
          precio_lista2: tarjeta,
          precio_venta: efectivo || tarjeta || 0,
          referencia: p.reference || '',
          producto_inventariable: p.stock_control !== false,
          impuesto_cargo: impuestoNombre,
          incluir_iva_precio: p.tax_included || false,
          active: p.active !== false,
          warehouses: p.warehouses || []
        };
      });
      setProducts(mapped);
    }
  }, [siigoProducts, products.length]);

  const loadSiigoData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const startTime = performance.now();
      
      // 1. Cargar productos: cache localStorage > contexto compartido > API
      const productsCache = localStorage.getItem('siigo_productos_cache');
      if (productsCache) {
        try {
          setProducts(JSON.parse(productsCache));
        } catch (_) {
          // JSON corrupto, ignorar y seguir al fallback
        }
      } else if (siigoProducts && siigoProducts.length > 0) {
        // Fallback: mapear desde el contexto compartido al formato que espera Facturacion
        const mapped = siigoProducts.map(p => {
          const priceList = p.prices?.[0]?.price_list || [];
          const efectivo = priceList.find(pl => pl.position === 1)?.value || 0;
          const tarjeta = priceList.find(pl => pl.position === 2)?.value || 0;
          let impuestoNombre = 'No aplica';
          if (p.taxes && p.taxes.length > 0) {
            const taxId = p.taxes[0].id;
            if (taxId === 5848) impuestoNombre = 'IVA 19%';
            else if (taxId === 5849) impuestoNombre = 'IVA 5%';
            else if (taxId === 13939) impuestoNombre = 'IVA 0%';
            else impuestoNombre = p.taxes[0].name || 'No aplica';
          }
          return {
            id: p.id,
            codigo: p.code || '',
            nombre: p.name || '',
            categoria: p.account_group?.name || 'Sin categoría',
            cantidad: p.available_quantity || 0,
            precio_lista1: efectivo,
            precio_lista2: tarjeta,
            precio_venta: efectivo || tarjeta || 0,
            referencia: p.reference || '',
            producto_inventariable: p.stock_control !== false,
            impuesto_cargo: impuestoNombre,
            incluir_iva_precio: p.tax_included || false,
            active: p.active !== false,
            warehouses: p.warehouses || []
          };
        });
        setProducts(mapped);
      } else if (loadSiigoProducts) {
        // No hay nada en cache ni en contexto: disparar fetch del contexto compartido
        loadSiigoProducts();
      }
      
      // 2. Cargar datos de facturación desde caché PRIMERO
      if (isCacheValid(CACHE_KEY_FACTURACION)) {
        const cachedFacData = getCachedData(CACHE_KEY_FACTURACION);
        if (cachedFacData) {
          console.log('⚡ Usando datos de facturación desde caché');
          setDocumentTypes(cachedFacData.document_types || []);
          setWarehouses(cachedFacData.warehouses || []);
          setPaymentTypes(cachedFacData.payment_types || []);
          const usuariosActivos = (cachedFacData.users || []).filter(u => u.active === true);
          setUsers(usuariosActivos);
        }
      }
      
      // 3. Cargar datos de facturación frescos desde API (con retry si loading)
      const loadFacturacionData = async (retries = 3) => {
        try {
          const facDataRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/facturacion-data${forceRefresh ? '?force_refresh=true' : ''}`);
          const facData = await facDataRes.json();
          
          if (facData.success && facData.document_types?.length > 0) {
            setDocumentTypes(facData.document_types || []);
            setWarehouses(facData.warehouses || []);
            setPaymentTypes(facData.payment_types || []);
            const usuariosActivos = (facData.users || []).filter(u => u.active === true);
            setUsers(usuariosActivos);
            
            setCachedData(CACHE_KEY_FACTURACION, {
              document_types: facData.document_types || [],
              warehouses: facData.warehouses || [],
              payment_types: facData.payment_types || [],
              users: facData.users || []
            });
          } else if (facData.loading && retries > 0) {
            // Datos sincronizandose, reintentar en 5 segundos
            setTimeout(() => loadFacturacionData(retries - 1), 5000);
          }
        } catch (e) {
          console.warn('Error cargando datos de facturacion:', e);
          if (retries > 0) setTimeout(() => loadFacturacionData(retries - 1), 5000);
        }
      };
      await loadFacturacionData();
      
      // 4. Los clientes ahora se manejan completamente por OptimizedCustomerSelector
      // No necesitamos cargarlos aquí
      
      const endTime = performance.now();
      console.log(`⏱️ Tiempo de carga de facturación: ${Math.round(endTime - startTime)}ms`);
      
    } catch (error) {
      console.error('Error cargando datos de Siigo:', error);
      showToast('Error cargando datos de Siigo', true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSellerChange = (e) => {
    const sellerId = e.target.value;
    if (sellerId) {
      const seller = users.find(u => u.id === parseInt(sellerId));
      if (seller) {
        setSelectedSeller(seller);
        setSellerName(sellerId);
        // Guardar en localStorage para persistencia
        localStorage.setItem('facturacion_vendedor', JSON.stringify(seller));
      }
    } else {
      setSelectedSeller(null);
      setSellerName('');
      localStorage.removeItem('facturacion_vendedor');
    }
  };

  const calculateItemTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    const subtotal = qty * price;
    const discountAmount = subtotal * (item.discount / 100);
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = afterDiscount * (item.tax / 100);
    const retentionAmount = afterDiscount * (item.retention / 100);
    return afterDiscount + taxAmount - retentionAmount;
  };

  const calculateTotals = () => {
    let grossTotal = 0;
    let totalDiscounts = 0;

    items.forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      const itemSubtotal = qty * price;
      grossTotal += itemSubtotal;
      totalDiscounts += itemSubtotal * (item.discount / 100);
    });

    const subtotal = grossTotal - totalDiscounts;
    let netTotal = 0;

    items.forEach(item => {
      netTotal += calculateItemTotal(item);
    });

    return { grossTotal, totalDiscounts, subtotal, netTotal };
  };

  const totals = calculateTotals();
  const totalPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 3000);
  };

  const updateItemField = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        // Si se selecciona un producto, actualizar campos automáticamente
        if (field === 'product_code') {
          // Si se borra el código de producto, limpiar campos relacionados
          if (!value || value === '') {
            return {
              ...item,
              product_code: '',
              product_id: null,
              description: '',
              unitPrice: ''
            };
          }
          
          const selectedProduct = products.find(p => p.codigo === value);
          if (selectedProduct) {
            return {
              ...item,
              product_code: value,
              product_id: selectedProduct.id,
              description: selectedProduct.nombre,
              unitPrice: selectedProduct.precio_venta || ''
            };
          }
        }
        
        // Para cantidad y precio, mantener como string para permitir decimales con "."
        if (field === 'quantity' || field === 'unitPrice') {
          return {
            ...item,
            [field]: value
          };
        }
        
        // Para otros campos numéricos, convertir a número
        const numericFields = ['discount', 'tax', 'retention'];
        return {
          ...item,
          [field]: numericFields.includes(field) ? (parseFloat(value) || 0) : value
        };
      }
      return item;
    }));
  };

  const deleteItem = (itemId) => {
    if (items.length > 1) {
      setItems(items.filter(i => i.id !== itemId));
    }
  };

  const addItem = () => {
    setItems([...items, createNewItem()]);
  };

  const addPayment = () => {
    if (!paymentMethod) {
      showToast('⚠️ Por favor seleccione una forma de pago', true);
      return;
    }

    const amount = parseFloat(paymentAmount) || 0;
    if (amount <= 0) {
      showToast('⚠️ Por favor ingrese un monto válido', true);
      return;
    }

    // Buscar nombre de forma de pago en Siigo o usar fallback
    const siigoPaymentType = paymentTypes.find(pt => pt.id.toString() === paymentMethod.toString());
    if (!siigoPaymentType) {
      showToast('⚠️ Forma de pago no válida. Presione "Sincronizar" para cargar las de Siigo', true);
      return;
    }

    const existing = payments.find(p => p.method.toString() === paymentMethod.toString());
    if (existing) {
      setPayments(payments.map(p => p === existing ? { ...p, amount: Math.round((p.amount + amount) * 100) / 100 } : p));
    } else {
      setPayments([...payments, {
        method: paymentMethod,
        methodName: `💳 ${siigoPaymentType.name}`,
        amount: amount
      }]);
    }

    setPaymentMethod('');
    setPaymentAmount('');
  };

  const deletePayment = (index) => {
    setPayments(payments.filter((_, i) => i !== index));
  };

  // Función para obtener stock de una bodega específica para un producto
  const getWarehouseStock = (product, warehouseId) => {
    if (!product || !product.warehouses) return 0;
    const warehouse = product.warehouses.find(w => w.id === parseInt(warehouseId));
    return warehouse?.quantity || 0;
  };

  // Función para obtener precio mínimo sugerido (efectivo)
  const getMinimumPrice = (product) => {
    if (!product) return 0;
    // precio_lista1 es el precio efectivo (mínimo permitido), dividido por 1.19 para quitar IVA
    return (product.precio_lista1 || 0) / 1.19;
  };

  const saveInvoice = async (sendAfterSave = false) => {
    // ============================================
    // VALIDACIONES DE CAMPOS OBLIGATORIOS
    // ============================================
    
    if (!documentType) {
      showToast('⚠️ Por favor seleccione el Tipo de Documento', true);
      return;
    }
    
    if (!clientId) {
      showToast('⚠️ Por favor seleccione un Cliente', true);
      return;
    }
    
    if (items.length === 0 || !items.some(i => i.product_code)) {
      showToast('⚠️ Debe agregar al menos un producto', true);
      return;
    }

    // Validar cada item
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.product_code) continue; // Ignorar items vacíos
      
      const itemNum = i + 1;
      
      // Validar Código de Producto
      if (!item.product_code) {
        showToast(`⚠️ Producto #${itemNum}: Falta el Código de Producto`, true);
        return;
      }
      
      // Validar Bodega
      if (!item.warehouse) {
        showToast(`⚠️ Producto #${itemNum}: Seleccione una Bodega/Almacén`, true);
        return;
      }
      
      // Validar Cantidad
      const cantidadValidar = parseFloat(item.quantity) || 0;
      if (!item.quantity || cantidadValidar <= 0) {
        showToast(`⚠️ Producto #${itemNum}: Ingrese una Cantidad válida`, true);
        return;
      }
      
      // Validar Valor Unitario
      const precioValidar = parseFloat(item.unitPrice) || 0;
      if (!item.unitPrice || precioValidar <= 0) {
        showToast(`⚠️ Producto #${itemNum}: Ingrese un Valor Unitario válido`, true);
        return;
      }
      
      // ============================================
      // VALIDACIÓN: Cantidad ≤ Stock en bodega
      // ============================================
      const selectedProduct = products.find(p => p.codigo === item.product_code);
      if (selectedProduct) {
        const stockBodega = getWarehouseStock(selectedProduct, item.warehouse);
        const cantidadNum = parseFloat(item.quantity) || 0;
        if (cantidadNum > stockBodega) {
          const bodegaName = warehouses.find(w => w.id === parseInt(item.warehouse))?.name || 'seleccionada';
          showToast(`❌ Producto #${itemNum}: La cantidad (${cantidadNum}) excede el stock disponible en ${bodegaName} (${stockBodega} unidades)`, true);
          return;
        }
        
        // ============================================
        // VALIDACIÓN: Precio ≥ Precio mínimo (efectivo) con tolerancia de 1 peso
        // ============================================
        const precioMinimo = getMinimumPrice(selectedProduct);
        if (parseFloat(item.unitPrice) < (precioMinimo - 1)) {
          const precioMinimoFormateado = precioMinimo.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
          const precioActualFormateado = parseFloat(item.unitPrice).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
          showToast(`❌ Producto #${itemNum}: El precio (${precioActualFormateado}) es menor al mínimo permitido (${precioMinimoFormateado})`, true);
          return;
        }
      }
    }

    // ============================================
    // VALIDACIÓN: Formas de Pago obligatorias
    // ============================================
    if (payments.length === 0) {
      showToast('⚠️ Debe agregar al menos una Forma de Pago', true);
      return;
    }

    // ============================================
    // VALIDACIÓN: Total Pagos = Total Neto (tolerancia de $10 por redondeos)
    // ============================================
    const diferenciaPago = Math.abs(totalPayments - totals.netTotal);
    if (diferenciaPago > 10) {
      const totalPagosFormateado = totalPayments.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
      const totalNetoFormateado = totals.netTotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
      showToast(`❌ El total de formas de pago (${totalPagosFormateado}) debe ser igual al Total Neto (${totalNetoFormateado})`, true);
      return;
    }
    
    try {
      setLoading(true);
      
      // Obtener información del cliente y vendedor seleccionado
      const selectedClient = customers.find(c => c.identification === clientId);
      const selectedSeller = users.find(u => u.id === parseInt(sellerName));
      
      // Preparar datos para Siigo
      const invoiceData = {
        document: {
          id: documentType
        },
        date: invoiceDate,
        customer: {
          identification: clientId,
          branch_office: 0
        },
        // cost_center: 235,  // Deshabilitado - no permitido en configuración de documento
        seller: sellerName ? parseInt(sellerName) : undefined,
        observations: observations,
        items: items.filter(i => i.product_code).map(item => {
          // Mapear el porcentaje de IVA al ID de impuesto de Siigo
          const taxPercent = parseFloat(item.tax) || 0;
          let taxId;
          if (taxPercent === 19) {
            taxId = 5848; // IVA 19%
          } else if (taxPercent === 5) {
            taxId = 5849; // IVA 5%
          } else {
            taxId = 13939; // IVA 0% (Excluido)
          }
          
          // Mapear retención al ID de Siigo
          const retentionPercent = parseFloat(item.retention) || 0;
          const retentionMap = {
            11: 5850, 10: 5851, 6: 5852, 4: 5853, 
            2.5: 5854, 3.5: 5865, 7: 5866, 2: 5867, 1: 5868
          };
          const retentionId = retentionMap[retentionPercent];
          
          const taxes = [{ id: taxId }];
          if (retentionId) {
            taxes.push({ id: retentionId });
          }
          
          // Redondear precio a 4 decimales para mayor precisión con Siigo
          const priceRounded = Math.round((parseFloat(item.unitPrice) || 0) * 10000) / 10000;
          const qtyRounded = Math.round((parseFloat(item.quantity) || 0) * 10000) / 10000;
          
          return {
            code: item.product_code,
            description: item.description,
            quantity: qtyRounded,
            price: priceRounded,
            discount: item.discount,
            warehouse: item.warehouse ? parseInt(item.warehouse) : undefined,
            taxes: taxes
          };
        }),
        payments: (() => {
          // Calcular total EXACTO desde los items como Siigo los recibirá
          let totalFactura = 0;
          items.filter(i => i.product_code).forEach(item => {
            const price = Math.round((parseFloat(item.unitPrice) || 0) * 10000) / 10000;
            const qty = Math.round((parseFloat(item.quantity) || 0) * 10000) / 10000;
            const base = qty * price;
            const discountAmt = base * ((parseFloat(item.discount) || 0) / 100);
            const afterDiscount = base - discountAmt;
            const taxAmt = afterDiscount * ((parseFloat(item.tax) || 0) / 100);
            const retAmt = afterDiscount * ((parseFloat(item.retention) || 0) / 100);
            totalFactura += afterDiscount + taxAmt - retAmt;
          });
          totalFactura = Math.round(totalFactura * 100) / 100;
          
          if (payments.length === 1) {
            return [{ id: parseInt(payments[0].method), value: totalFactura, due_date: invoiceDate }];
          }
          
          // Para multiples pagos, ajustar el ultimo para que sume exactamente el total
          const paymentsList = payments.map((p) => ({
            id: parseInt(p.method),
            value: Math.round((parseFloat(p.amount) || 0) * 100) / 100,
            due_date: invoiceDate
          }));
          
          // Ajustar el ultimo pago para que la suma sea exacta
          const sumaSinUltimo = paymentsList.slice(0, -1).reduce((sum, p) => sum + p.value, 0);
          if (paymentsList.length > 0) {
            paymentsList[paymentsList.length - 1].value = Math.round((totalFactura - sumaSinUltimo) * 100) / 100;
          }
          
          return paymentsList;
        })(),
        // Información adicional para registro local
        customer_name: selectedClient?.commercial_name || selectedClient?.name || clientName || 'Cliente',
        customer_identification: clientId,
        seller_name: selectedSeller ? `${selectedSeller.first_name} ${selectedSeller.last_name}` : '',
        created_by: 'Sistema',
        stamp: sendAfterSave  // true = timbrar y enviar por email
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast(sendAfterSave ? '✅ Factura guardada y enviada exitosamente' : '✅ Factura guardada exitosamente');
        
        // Reset form
        setItems([createNewItem()]);
        setPayments([]);
        setDocumentType('');
        setClientId('');
        setClientName('');
        setInvoiceNumber('');
        setSellerName('');
        setObservations('');
      } else {
        showToast(`❌ Error: ${data.error}`, true);
      }
    } catch (error) {
      console.error('Error guardando factura:', error);
      showToast('❌ Error guardando factura', true);
    } finally {
      setLoading(false);
    }
  };

  const cancelInvoice = () => {
    if (items.some(item => item.product_code)) {
      if (window.confirm('¿Cancelar factura? Se perderán todos los datos ingresados.')) {
        setItems([createNewItem()]);
        setPayments([]);
        setDocumentType('');
        setClientId('');
        setClientName('');
        setInvoiceNumber('');
        setSellerName('');
        setObservations('');
        showToast('Factura cancelada');
      }
    }
  };

  const handleSaveNewClient = async (customerData) => {
    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/siigo/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showToast('✅ Cliente creado exitosamente en Siigo');
        setShowClientModal(false);
        
        const created = data.customer;
        if (created) {
          setCustomers(prev => [created, ...prev.filter(c => c.identification !== created.identification)]);
          setClientId(created.identification);
          setClientName(Array.isArray(created.name) ? created.name.join(' ') : created.name);
        }
      } else {
        showToast(`❌ Error: ${data.error}`, true);
      }
    } catch (error) {
      console.error('Error creando cliente:', error);
      showToast('❌ Error al crear cliente', true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-md border-l-4 border-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <FileText className="text-purple-600" size={32} />
                  Nueva Factura de Venta
                </h1>
                <p className="text-gray-600">Complete los datos para generar su factura en Siigo</p>
              </div>
              
              {/* Selector de Vendedor */}
              <div className="ml-8">
                <label className="block text-sm font-medium mb-2 text-gray-700">👤 Vendedor</label>
                <select
                  value={sellerName}
                  onChange={handleSellerChange}
                  className="min-w-[250px] p-3 border-2 border-purple-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 bg-white"
                >
                  <option value="">Seleccionar vendedor...</option>
                  {users && Array.isArray(users) && users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
                {selectedSeller && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Vendedor seleccionado: {selectedSeller.first_name} {selectedSeller.last_name}
                  </p>
                )}
              </div>
            </div>
            
            {loading && (
              <div className="flex items-center gap-2 text-blue-600">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Cargando...</span>
              </div>
            )}
            {!loading && (
              <button
                onClick={forceRefreshData}
                data-testid="facturacion-sync-btn"
                className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                title="Recargar datos de Siigo"
              >
                🔄 Sincronizar
              </button>
            )}
          </div>
        </div>

        {/* Información General */}
        <div className="bg-white rounded-xl p-6 mb-5 shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <User size={24} className="text-purple-600" />
            Información General
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Tipo de Documento</label>
              <select
                value={documentType}
                onChange={(e) => {
                  const selectedDoc = documentTypes.find(dt => dt.id === parseInt(e.target.value));
                  setDocumentType(e.target.value);
                  // Actualizar número de documento automáticamente
                  if (selectedDoc) {
                    setInvoiceNumber(selectedDoc.consecutive || '');
                  }
                }}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              >
                <option value="">📄 Seleccionar tipo de documento...</option>
                {documentTypes && Array.isArray(documentTypes) && documentTypes.map(dt => (
                  <option key={dt.id} value={dt.id}>
                    {dt.type} - {dt.code} - {dt.description}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Cliente</label>
                <button
                  type="button"
                  onClick={() => setShowClientModal(true)}
                  data-testid="new-client-btn"
                  className="text-xs bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-md flex items-center gap-1 transition-colors"
                >
                  <Plus size={14} />
                  Crear nuevo
                </button>
              </div>
              
              {/* Selector de Cliente Simple */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Cliente</span>
                  {customersLoading ? (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Cargando...
                    </span>
                  ) : (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      ✓ {customers.length.toLocaleString()} clientes
                    </span>
                  )}
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={clientSearch || clientName}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                      if (!e.target.value) {
                        setClientId('');
                        setClientName('');
                      }
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="👤 Buscar por nombre o NIT..."
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                
                {/* Dropdown de resultados */}
                {showClientDropdown && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {filteredCustomers.map((customer) => {
                      const fullName = Array.isArray(customer.name) ? customer.name.join(' ') : (customer.name || '');
                      return (
                        <div
                          key={customer.id}
                          onClick={() => {
                            setClientId(customer.identification);
                            setClientName(fullName);
                            setClientSearch('');
                            setShowClientDropdown(false);
                          }}
                          className="px-4 py-3 cursor-pointer hover:bg-indigo-50 border-b last:border-b-0"
                        >
                          <p className="font-medium">{fullName}</p>
                          <p className="text-sm text-gray-500">NIT: {customer.identification}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Mostrar cliente seleccionado */}
                {clientName && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-300 rounded-lg flex items-center justify-between">
                    <div>
                      <span className="text-sm text-green-700 font-semibold">✓ Cliente seleccionado:</span>
                      <div className="text-sm text-gray-700 mt-1">
                        <span className="font-bold">{clientName}</span> ({clientId})
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setClientId('');
                        setClientName('');
                        setClientSearch('');
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Limpiar selección"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Fecha de Elaboración</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Número de documento</label>
              <input
                type="text"
                value={invoiceNumber}
                readOnly
                placeholder="📄 Seleccione tipo de documento para ver número..."
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 font-semibold"
                title="Numeración automática de Siigo"
              />
              {invoiceNumber && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Próximo número consecutivo de Siigo
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Productos y Servicios */}
        <div className="bg-white rounded-xl p-6 mb-5 shadow-md">
          <div className="flex justify-between items-center mb-5">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Package size={24} className="text-purple-600" />
              Productos y Servicios
            </h2>
            <span className="bg-purple-100 text-purple-800 px-3 py-1.5 rounded-lg text-sm font-semibold">
              Total Items: {items.length}
            </span>
          </div>

          {items.map((item, index) => (
            <ItemCard
              key={item.id}
              item={item}
              index={index}
              products={products}
              warehouses={warehouses}
              canDelete={items.length > 1}
              onUpdate={updateItemField}
              onDelete={deleteItem}
              calculateItemTotal={calculateItemTotal}
              isLast={index === items.length - 1}
            />
          ))}

          <button
            onClick={addItem}
            className="w-full p-3 bg-white text-purple-600 border-2 border-purple-500 rounded-lg font-semibold hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={20} />
            Agregar Nuevo Producto/Servicio
          </button>
        </div>

        {/* Formas de Pago */}
        <div className="bg-white rounded-xl p-6 mb-5 shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
            <DollarSign size={24} className="text-purple-600" />
            Formas de Pago
          </h2>
          
          <div className="flex gap-3 mb-4 flex-wrap">
            <select
              value={paymentMethod}
              onChange={(e) => {
                setPaymentMethod(e.target.value);
                // Auto-llenar con el valor pendiente por pagar
                const pendingAmount = totals.netTotal - totalPayments;
                if (pendingAmount > 0) {
                  setPaymentAmount(pendingAmount.toFixed(0));
                }
              }}
              data-testid="payment-method-select"
              className="flex-1 min-w-[200px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            >
              <option value="">{paymentTypes.length > 0 ? '💳 Selecciona forma de pago' : '⏳ Cargando formas de pago de Siigo...'}</option>
              {paymentTypes.map(pt => (
                <option key={pt.id} value={pt.id}>{pt.name}</option>
              ))}
            </select>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              data-testid="payment-amount-input"
              placeholder={`Pendiente: $${Math.max(0, totals.netTotal - totalPayments).toLocaleString('es-CO')}`}
              step="1"
              min="0"
              className="flex-1 min-w-[150px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
            />
            <button
              onClick={addPayment}
              data-testid="add-payment-btn"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Agregar Forma de Pago
            </button>
          </div>

          <div className="mb-4">
            {payments.map((payment, index) => (
              <div key={index} data-testid={`payment-row-${index}`} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg mb-2">
                <span className="flex-1 font-semibold text-gray-700">{payment.methodName}</span>
                <span className="font-bold text-purple-600">{payment.amount.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                <button
                  onClick={() => deletePayment(index)}
                  data-testid={`delete-payment-${index}`}
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-700">Total formas de pagos:</span>
              <span className="text-xl font-bold text-purple-600">{totalPayments.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
            {/* Indicador de diferencia con Total Neto - solo mostrar si hay productos con valores */}
            {(() => {
              const diferencia = totalPayments - totals.netTotal;
              const hayProductos = items.some(i => i.product_code && parseFloat(i.quantity) > 0 && parseFloat(i.unitPrice) > 0);
              
              if (!hayProductos || totals.netTotal <= 0) return null;
              
              if (Math.abs(diferencia) <= 10) {
                // Correcto (tolerancia de $10)
                return totalPayments > 0 ? (
                  <div className="mt-2 p-2 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                    ✅ Total de pagos correcto
                  </div>
                ) : null;
              } else if (diferencia < 0) {
                // Falta por pagar
                return (
                  <div className="mt-2 p-2 rounded-lg text-sm font-medium bg-yellow-100 text-yellow-800">
                    ⚠️ Falta por pagar: {Math.abs(diferencia).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                );
              } else {
                // Excede
                return (
                  <div className="mt-2 p-2 rounded-lg text-sm font-medium bg-red-100 text-red-800">
                    ❌ Excede el total: {diferencia.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </div>
                );
              }
            })()}
          </div>
        </div>

        {/* Observaciones */}
        <div className="bg-white rounded-xl p-6 mb-5 shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            📝 Observaciones
          </h2>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows="4"
            maxLength="500"
            placeholder="Escriba aquí las observaciones de la factura (opcional)..."
            className="w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-purple-500 focus:ring-purple-200 resize-none text-gray-700"
          />
          <div className="flex justify-between mt-2 text-sm text-gray-500">
            <span>💡 Estas observaciones aparecerán en la factura de Siigo</span>
            <span>{observations.length}/500 caracteres</span>
          </div>
        </div>

        {/* Totales de Factura */}
        <div className="bg-white rounded-xl p-6 mb-5 shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-5">Totales de Factura</h2>
          
          <div className="flex justify-between py-3 text-base">
            <span className="text-gray-600">Total Bruto:</span>
            <span className="font-semibold text-gray-700">{totals.grossTotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
          <div className="flex justify-between py-3 text-base">
            <span className="text-gray-600">Descuentos:</span>
            <span className="font-semibold text-red-500">-{totals.totalDiscounts.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
          <div className="flex justify-between py-3 text-base">
            <span className="text-gray-600">Subtotal:</span>
            <span className="font-semibold text-gray-700">{totals.subtotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
          <div className="flex justify-between pt-4 mt-2 border-t-2 border-gray-200">
            <span className="text-lg font-bold text-green-600">Total Neto:</span>
            <span className="text-lg font-bold text-green-600">{totals.netTotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex gap-4 justify-end flex-wrap">
          <button
            onClick={cancelInvoice}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <X size={20} />
            Cancelar
          </button>
          <button
            onClick={() => saveInvoice(false)}
            disabled={loading}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
          >
            <Save size={20} />
            Guardar en Siigo
          </button>
          <button
            onClick={() => saveInvoice(true)}
            disabled={loading}
            className="px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:bg-gray-400 flex items-center gap-2"
          >
            <Send size={20} />
            Guardar y Enviar
          </button>
        </div>
      </div>

      {/* Modal de Nuevo Cliente */}
      <NewClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        onSave={handleSaveNewClient}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 ${toast.isError ? 'bg-red-500' : 'bg-green-500'} text-white px-6 py-4 rounded-lg shadow-lg animate-slide-in z-50 flex items-center gap-2`}
        >
          {toast.isError ? <AlertCircle size={20} /> : <FileText size={20} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

const ItemCard = ({ item, index, products, warehouses, canDelete, onUpdate, onDelete, calculateItemTotal, isLast }) => {
  // Solo expandir automáticamente si es el último item
  const [isExpanded, setIsExpanded] = useState(isLast);
  const [searchCode, setSearchCode] = useState(null); // null = mostrar product_code, '' = campo vacío
  
  // Colapsar automáticamente cuando deja de ser el último
  React.useEffect(() => {
    if (!isLast && item.product_code) {
      setIsExpanded(false);
    }
  }, [isLast, item.product_code]);
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState(''); // Para mostrar opción seleccionada
  const itemTotal = calculateItemTotal(item);

  // Filtrar productos mientras se escribe - solo mostrar productos ACTIVOS
  React.useEffect(() => {
    // Filtrar solo productos activos
    const activeProducts = products.filter(p => p.active !== false);
    
    if (searchCode !== null && searchCode !== '') {
      const filtered = activeProducts.filter(p => 
        p.codigo.toLowerCase().includes(searchCode.toLowerCase()) ||
        p.nombre.toLowerCase().includes(searchCode.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(activeProducts);
    }
  }, [searchCode, products]);

  // Encontrar producto seleccionado
  const selectedProduct = products.find(p => p.codigo === item.product_code);

  // Encontrar bodega con mayor cantidad del producto
  const maxQuantityWarehouse = selectedProduct && warehouses.length > 0
    ? warehouses.reduce((max, w) => w.quantity > (max.quantity || 0) ? w : max, warehouses[0])
    : null;

  // Manejar selección de precio sugerido - dividir por 1.19 automáticamente
  const handlePriceSelect = (priceType) => {
    if (selectedProduct && priceType) {
      const basePrice = priceType === 'efectivo' ? selectedProduct.precio_lista1 : selectedProduct.precio_lista2;
      // Dividir automáticamente por 1.19 para quitar IVA incluido
      const priceWithoutIVA = basePrice / 1.19;
      // Usar 4 decimales para mayor precisión con Siigo
      onUpdate(item.id, 'unitPrice', priceWithoutIVA.toFixed(4));
      setSelectedPriceType(priceType);
    }
  };

  // Manejar cambio manual de precio - dividir por 1.19 automáticamente
  const handlePriceChange = (newPrice) => {
    if (newPrice && parseFloat(newPrice) > 0) {
      // Dividir automáticamente por 1.19 con 4 decimales para mayor precisión
      const priceWithoutIVA = parseFloat(newPrice) / 1.19;
      onUpdate(item.id, 'unitPrice', priceWithoutIVA.toFixed(4));
    } else {
      onUpdate(item.id, 'unitPrice', newPrice);
    }
  };

  // Calcular precio unitario con IVA para el resumen (el itemTotal ya incluye IVA)
  const taxPercent = parseFloat(item.tax) || 0;
  const unitPriceWithIVA = (parseFloat(item.unitPrice) || 0) * (1 + taxPercent / 100);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-4">
      <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-gray-200">
        <h3 className="text-base font-bold text-gray-700 cursor-pointer flex-1" onClick={() => setIsExpanded(!isExpanded)}>
          Producto #{index + 1} {isExpanded ? '🔼' : '🔽'}
        </h3>
        
        {/* Resumen compacto cuando está colapsado */}
        {!isExpanded && item.product_code && (
          <div className="flex-1 mx-4 text-sm">
            <span className="text-gray-700 font-medium truncate block max-w-md">{item.description || 'Sin descripción'}</span>
            <span className="text-gray-500">
              Cant: {item.quantity || 0} | Und: {unitPriceWithIVA.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })} | 
              Total: <span className="font-bold text-purple-700">{itemTotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </span>
          </div>
        )}
        
        {canDelete && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {isExpanded && (
        <>
          {/* Código de Producto con búsqueda filtrable */}
          <div className="mb-4 relative">
            <label className="block text-sm font-medium mb-2 text-gray-700">📦 Código de Producto</label>
            <div className="relative">
              <input
                type="text"
                value={searchCode !== null ? searchCode : item.product_code}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setSearchCode(newValue);
                  setShowDropdown(true);
                  // Si se borra completamente, limpiar el producto seleccionado
                  if (newValue === '') {
                    onUpdate(item.id, 'product_code', ''); // Esto limpia todo
                    setSelectedPriceType('');
                  }
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="🔍 Escribir para buscar..."
                className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              />
              {/* Botón para limpiar el campo */}
              {(searchCode || item.product_code) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchCode('');
                    onUpdate(item.id, 'product_code', ''); // Esto limpia product_code, description y unitPrice
                    setSelectedPriceType('');
                    setShowDropdown(false);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Limpiar producto"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Dropdown filtrado - Virtualizado */}
            {showDropdown && filteredProducts.length > 0 && (
              <VirtualizedProductDropdown
                products={filteredProducts.slice(0, 100)}
                onSelect={(product) => {
                  onUpdate(item.id, 'product_code', product.codigo);
                  setSearchCode(null);
                  setShowDropdown(false);
                }}
              />
            )}
          </div>

          {/* Descripción - READONLY */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700">📝 Descripción</label>
            <textarea
              value={item.description}
              readOnly
              placeholder="Se auto-llenará al seleccionar producto..."
              className="w-full p-3 border border-gray-300 rounded-lg min-h-[80px] resize-y bg-gray-100 text-gray-700 cursor-not-allowed"
            />
          </div>

          {/* Bodega con cantidades del producto actual */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-gray-700">🏭 Bodega/Almacén <span className="text-red-500">*</span></label>
            {selectedProduct ? (
              <>
                <select
                  value={item.warehouse}
                  onChange={(e) => onUpdate(item.id, 'warehouse', e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    !item.warehouse 
                      ? 'border-orange-400 bg-orange-50 focus:border-orange-500 focus:ring-orange-200' 
                      : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                  }`}
                >
                  <option value="">⚠️ Seleccionar bodega (obligatorio)</option>
                  {warehouses.map(w => {
                    const productWarehouse = selectedProduct.warehouses?.find(pw => pw.id === w.id);
                    const quantity = productWarehouse?.quantity || 0;
                    const hasStock = quantity > 0;
                    
                    return (
                      <option 
                        key={w.id} 
                        value={w.id}
                        style={{
                          backgroundColor: hasStock ? '#86efac' : 'white',
                          fontWeight: hasStock ? 'bold' : 'normal'
                        }}
                      >
                        {w.name} - {quantity.toFixed(2)} unidades
                      </option>
                    );
                  })}
                </select>
                {!item.warehouse && (
                  <p className="text-xs text-orange-600 mt-1 font-medium">⚠️ Debe seleccionar una bodega para facturar</p>
                )}
                <p className="text-xs text-blue-600 mt-1">
                  📦 Stock total disponible: {selectedProduct.cantidad.toFixed(2)} unidades
                </p>
              </>
            ) : (
              <select
                disabled
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
              >
                <option>Seleccione un producto primero</option>
              </select>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">📊 Cantidad</label>
              <input
                type="text"
                inputMode="decimal"
                value={item.quantity}
                onChange={(e) => {
                  // Permitir solo números, punto y coma decimal
                  const value = e.target.value.replace(',', '.');
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    onUpdate(item.id, 'quantity', value);
                  }
                }}
                placeholder="Ingrese cantidad..."
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  selectedProduct && item.warehouse && parseFloat(item.quantity) > (selectedProduct.warehouses?.find(w => w.id === parseInt(item.warehouse))?.quantity || 0)
                    ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                }`}
              />
              {/* Validación visual de cantidad vs stock */}
              {selectedProduct && item.warehouse && (() => {
                const stockBodega = selectedProduct.warehouses?.find(w => w.id === parseInt(item.warehouse))?.quantity || 0;
                if (parseFloat(item.quantity) > stockBodega) {
                  return (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ❌ Excede stock disponible ({stockBodega} unidades)
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">💵 Valor Unitario (Ingrese precio con IVA)</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  {(() => {
                    const precioMinimo = selectedProduct ? (selectedProduct.precio_lista1 || 0) / 1.19 : 0;
                    // Tolerancia de 1 peso para evitar falsos positivos por redondeo
                    const precioMenorAlMinimo = selectedProduct && parseFloat(item.unitPrice) > 0 && (parseFloat(item.unitPrice) < (precioMinimo - 1));
                    const precioConIVA = (parseFloat(item.unitPrice) || 0) * 1.19;
                    return (
                      <>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onBlur={(e) => {
                            if (e.target.value && parseFloat(e.target.value) > 0) {
                              // Dividir automáticamente por 1.19 al salir del campo (con 4 decimales para Siigo)
                              const priceWithoutIVA = parseFloat(e.target.value) / 1.19;
                              onUpdate(item.id, 'unitPrice', priceWithoutIVA.toFixed(4));
                            }
                          }}
                          onChange={(e) => onUpdate(item.id, 'unitPrice', e.target.value)}
                          min="0"
                          step="0.0001"
                          placeholder="Ej: 69000 (se restará IVA 19%)"
                          className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                            precioMenorAlMinimo
                              ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-200'
                              : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                          }`}
                        />
                        {parseFloat(item.unitPrice) > 0 && (
                          <p className="text-xs text-green-600 mt-1 font-medium">
                            ✅ Precio sin IVA: {parseFloat(item.unitPrice).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })} → 
                            Con IVA: {precioConIVA.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })}
                          </p>
                        )}
                        {precioMenorAlMinimo && (
                          <p className="text-xs text-red-600 mt-1 font-medium">
                            ❌ Precio menor al mínimo ({precioMinimo.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 })})
                          </p>
                        )}
                        {!parseFloat(item.unitPrice) && (
                          <p className="text-xs text-blue-600 mt-1">💡 Ingrese el precio CON IVA, se restará automáticamente el 19%</p>
                        )}
                      </>
                    );
                  })()}
                </div>
                {selectedProduct && (
                  <select
                    value={selectedPriceType}
                    onChange={(e) => {
                      handlePriceSelect(e.target.value);
                    }}
                    className="w-32 p-3 text-sm border border-purple-400 rounded-lg bg-purple-50 focus:outline-none focus:border-purple-500"
                  >
                    <option value="">💡 Sugerido</option>
                    <option value="efectivo">💵 Efectivo</option>
                    <option value="tarjeta">💳 Tarjeta</option>
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">🏷️ % Descuento</label>
              <input
                type="number"
                value={item.discount}
                onChange={(e) => onUpdate(item.id, 'discount', e.target.value)}
                min="0"
                max="100"
                step="0.01"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">📈 % IVA</label>
              <select
                value={item.tax}
                onChange={(e) => onUpdate(item.id, 'tax', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              >
                <option value="0">Sin impuesto (0%)</option>
                <option value="5">IVA 5%</option>
                <option value="19">IVA 19%</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">📉 % Retención</label>
              <select
                value={item.retention}
                onChange={(e) => onUpdate(item.id, 'retention', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
              >
                <option value="0">Ninguno</option>
                <option value="11">Retefuente 11%</option>
                <option value="10">Retefuente 10%</option>
                <option value="7">Retefuente 7%</option>
                <option value="6">Retefuente 6%</option>
                <option value="4">Retefuente 4%</option>
                <option value="3.5">Retefuente 3.5%</option>
                <option value="2.5">Retefuente 2.5%</option>
                <option value="2">Retefuente 2%</option>
                <option value="1">Retefuente 1%</option>
              </select>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-green-800">💰 Total Item:</span>
              <span className="text-2xl font-extrabold text-green-900">{itemTotal.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Facturacion;
